import React, { Dispatch, JSX, SetStateAction, useEffect, useRef } from "react";
import { UStyles } from "@/app/utils/UStyles";
import { Limits } from "@/app/vars/Limits";
import Portal from "../Portal/Portal";
import styles from "./Popover.module.css";
import PopoverContent, {
  PopoverContentPosition,
  PopoverContentProps,
  Position,
} from "./PopoverContent";
import PopoverTrigger, { PopoverTriggerProps } from "./PopoverTrigger";

type PopoverProps = {
  isVisible: boolean;
  setIsVisible: Dispatch<SetStateAction<boolean>>;
  openAfterClick?: boolean;
  cancelToggleAfterClick?: boolean;
  closeWhenClickOutside?: boolean;
  children: [React.ReactElement<PopoverContentProps>, React.ReactElement<PopoverTriggerProps>];
  className?: string;
  isPortaled?: boolean;
};

export default function Popover({
  isVisible = false,
  setIsVisible,
  openAfterClick = false,
  cancelToggleAfterClick = false,
  closeWhenClickOutside = false,
  children,
  className = "",
  isPortaled = false,
}: PopoverProps): JSX.Element {
  const popoverRef = useRef(null);
  const popoverContentRef = useRef(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  let contentPosition: undefined | PopoverContentPosition;

  const showPopover = () => {
    if (!openAfterClick) {
      setIsVisible(true);
    }
  };

  const hidePopover = () => {
    if (!openAfterClick) {
      setIsVisible(false);
    }
  };

  const togglePopover = (e: React.MouseEvent<HTMLElement>) => {
    if (isVisible && cancelToggleAfterClick) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    if (openAfterClick) {
      setIsVisible(!isVisible);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        closeWhenClickOutside &&
        popoverRef.current &&
        e.target &&
        !(popoverRef.current as Node).contains(e.target as Node) &&
        (!isPortaled ||
          (popoverContentRef.current &&
            !(popoverContentRef.current as Node).contains(e.target as Node)))
      ) {
        setIsVisible(false);
      }
    };

    if (closeWhenClickOutside) {
      document.addEventListener("click", handleClickOutside);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }

    const handleContentPosition = () => {
      if (popoverContentRef.current) {
        const contentElement = popoverContentRef.current as HTMLElement;

        if (isPortaled && triggerRef.current) {
          const triggerElement =
            triggerRef.current.children.length === 1
              ? (triggerRef.current.firstElementChild as HTMLElement)
              : triggerRef.current;
          const triggerRect = triggerElement.getBoundingClientRect();

          const viewportWidth = document.documentElement.clientWidth;
          const minWidth = `${triggerRect.width}px`;
          const maxWidth = `${viewportWidth - Limits.POPOVER_VIEWPORT_GAP * 2}px`;

          contentElement.style.minWidth = minWidth;
          contentElement.style.maxWidth = maxWidth;

          for (const card of Array.from(contentElement.children)) {
            (card as HTMLElement).style.minWidth = minWidth;
            (card as HTMLElement).style.maxWidth = maxWidth;
          }

          contentElement.style.position = "fixed";
          contentElement.style.top = "";
          contentElement.style.bottom = "";
          contentElement.style.left = "";
          contentElement.style.right = "";

          if (contentPosition?.default) {
            if (contentPosition.default.bottom) {
              const rawOffset =
                contentPosition.default.bottom
                  .replace("100%", "")
                  .replace("calc(", "")
                  .replace(")", "")
                  .trim() || "0px";
              const offset =
                rawOffset.startsWith("+") || rawOffset.startsWith("-")
                  ? rawOffset
                  : `+ ${rawOffset}`;
              contentElement.style.bottom = `calc(${window.innerHeight - triggerRect.top}px ${offset})`;
            } else if (contentPosition.default.top) {
              const rawOffset =
                contentPosition.default.top
                  .replace("100%", "")
                  .replace("calc(", "")
                  .replace(")", "")
                  .trim() || "0px";
              const offset =
                rawOffset.startsWith("+") || rawOffset.startsWith("-")
                  ? rawOffset
                  : `+ ${rawOffset}`;

              const contentHeight = contentElement.offsetHeight;
              const spaceBelow = window.innerHeight - triggerRect.bottom;
              const flipsUp =
                contentHeight > 0 && spaceBelow < contentHeight && triggerRect.top > spaceBelow;

              if (flipsUp) {
                contentElement.style.bottom = `calc(${window.innerHeight - triggerRect.top}px ${offset})`;
              } else {
                contentElement.style.top = `calc(${triggerRect.bottom}px ${offset})`;
              }
            }

            const contentWidth = contentElement.offsetWidth;
            const maxLeft = viewportWidth - contentWidth - Limits.POPOVER_VIEWPORT_GAP;

            if (contentPosition.default.left) {
              contentElement.style.left = `${Math.max(Limits.POPOVER_VIEWPORT_GAP, Math.min(triggerRect.left, maxLeft))}px`;
            } else if (contentPosition.default.right) {
              const right = viewportWidth - triggerRect.right;
              contentElement.style.right = `${Math.max(Limits.POPOVER_VIEWPORT_GAP, Math.min(right, maxLeft))}px`;
            }
          }
        } else {
          if (contentPosition?.top) {
            if (contentElement && contentPosition?.default) {
              setContentPosition(contentElement, contentPosition.default);
            }

            const triggerRect = contentElement.getBoundingClientRect();
            const windowHeight = window.innerHeight;

            if (contentElement) {
              if (
                contentPosition?.top &&
                triggerRect.top + contentElement.offsetHeight >
                  windowHeight - contentElement.offsetHeight
              ) {
                setContentPosition(contentElement, contentPosition.top);
              }
            }
          }
        }
      }
    };

    function setContentPosition(element: HTMLElement, position: Position) {
      element.style.top = position.top || "";
      element.style.bottom = position.bottom || "";
      element.style.left = position.left || "";
      element.style.right = position.right || "";
    }

    if (!isPortaled || isVisible) {
      handleContentPosition();
    }

    globalThis.addEventListener("scroll", handleContentPosition);
    globalThis.addEventListener("resize", handleContentPosition);

    return () => {
      document.removeEventListener("click", handleClickOutside);
      globalThis.removeEventListener("scroll", handleContentPosition);
      globalThis.removeEventListener("resize", handleContentPosition);
    };
  }, [
    setIsVisible,
    isVisible,
    isPortaled,
    contentPosition?.top,
    contentPosition?.default,
    closeWhenClickOutside,
  ]);

  return (
    <div
      ref={popoverRef}
      className={UStyles.classNames(styles.popover, className)}
      onMouseLeave={hidePopover}
    >
      {React.Children.map(children, (child, index) => {
        if (index === 0) {
          return (
            <PopoverTrigger>
              <div
                ref={triggerRef}
                className={child.props.className || ""}
                onClick={togglePopover}
                onMouseEnter={showPopover}
              >
                {child.props.children}
              </div>
            </PopoverTrigger>
          );
        }

        contentPosition = (child.props as PopoverContentProps)?.position;

        const content = (
          <div
            className={UStyles.classNames(
              styles.popoverContent,
              isPortaled ? styles.popoverContentPortaled : "",
              isVisible ? styles.popoverContentOpen : "",
              child.props.className ? child.props.className : "",
            )}
            ref={popoverContentRef}
            style={contentPosition?.default ? contentPosition.default : {}}
          >
            {child.props.children}
          </div>
        );

        return (
          <PopoverContent key={index}>
            {isPortaled ? <Portal>{content}</Portal> : content}
          </PopoverContent>
        );
      })}
    </div>
  );
}
